import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const TYPES = [
  { id: "vehicle", label: "Fleet Vehicle" },
  { id: "condo", label: "Penthouse Stay" },
  { id: "package", label: "Touchdown Royal" },
];

export const BookingSection = ({ catalog, selection }) => {
  const [form, setForm] = useState({
    type: "vehicle",
    item_id: "",
    start_date: "",
    end_date: "",
    guests: 2,
    airport_transfer: false,
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const items = useMemo(() => {
    if (!catalog) return [];
    if (form.type === "vehicle") return catalog.fleet;
    if (form.type === "condo") return [catalog.condo];
    return [catalog.package];
  }, [catalog, form.type]);

  useEffect(() => {
    if (items.length && !items.some((i) => i.id === form.item_id)) {
      setForm((f) => ({ ...f, item_id: items[0].id }));
    }
  }, [items, form.item_id]);

  useEffect(() => {
    if (selection) {
      setForm((f) => ({ ...f, type: selection.type, item_id: selection.itemId }));
      setResult(null);
    }
  }, [selection]);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setForm((f) => ({
        ...f,
        guest_name: f.guest_name || data.name || "",
        guest_email: f.guest_email || data.email || "",
      }));
    }).catch(() => {});
  }, []);

  const nights = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    const d = (new Date(form.end_date) - new Date(form.start_date)) / 86400000;
    return d > 0 ? d : 0;
  }, [form.start_date, form.end_date]);

  const total = useMemo(() => {
    if (!catalog || !form.item_id) return 0;
    if (form.type === "vehicle") {
      const v = catalog.fleet.find((x) => x.id === form.item_id);
      if (!v) return 0;
      return nights * v.daily_rate + (form.airport_transfer ? catalog.airport_transfer_fee : 0);
    }
    if (form.type === "condo") return nights * catalog.condo.nightly_rate + catalog.condo.cleaning_fee;
    return catalog.package.flat_rate;
  }, [catalog, form, nights]);

  const set = (k) => (e) => {
    const val = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (nights < 1) {
      toast.error("Please pick an end date after your start date.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { ...form, guests: Number(form.guests) });
      setResult(data);
      toast.success(`Request ${data.reference} received. Check your inbox.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="border-t border-gold/10 bg-carbon/40 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Reveal>
          <p className="eyebrow">Reserve Your Arrival</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-100 sm:text-4xl lg:text-5xl">
            Live Pricing. <span className="gold-gradient-text italic">One Request.</span>
          </h2>
        </Reveal>

        {result ? (
          <Reveal className="mt-14">
            <div className="spotlight-card mx-auto max-w-2xl p-10 text-center" data-testid="booking-success-panel">
              <CheckCircle2 size={44} className="mx-auto text-gold" />
              <h3 className="mt-6 font-serif text-3xl text-slate-100">Request Received</h3>
              <p className="mt-3 font-mono text-sm tracking-[0.3em] text-gold" data-testid="booking-reference">
                {result.reference}
              </p>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
                {result.item_name} · {result.start_date} → {result.end_date} · estimated{" "}
                <span className="text-gold">${result.total_price.toLocaleString()}</span>. Our concierge
                will confirm availability at {result.guest_email} shortly.
              </p>
              <button
                data-testid="booking-new-request-btn"
                onClick={() => setResult(null)}
                className="mt-8 rounded-full border border-gold/40 px-8 py-3 font-display text-xs font-semibold tracking-[0.25em] text-gold uppercase transition-all hover:bg-gold/10"
              >
                Make Another Request
              </button>
            </div>
          </Reveal>
        ) : (
          <form onSubmit={submit} className="mt-14 grid gap-10 lg:grid-cols-5" data-testid="booking-form">
            <div className="space-y-7 lg:col-span-3">
              <div>
                <span className="lux-label">Experience</span>
                <div className="flex flex-wrap gap-3">
                  {TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      data-testid={`booking-type-${t.id}`}
                      onClick={() => setForm((f) => ({ ...f, type: t.id, item_id: "" }))}
                      className={`rounded-full border px-5 py-2.5 font-display text-[11px] tracking-[0.2em] uppercase transition-all ${
                        form.type === t.id
                          ? "border-gold bg-gold text-obsidian"
                          : "border-gold/25 text-zinc-400 hover:border-gold/60 hover:text-gold"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="lux-label" htmlFor="booking-item">Selection</label>
                <select
                  id="booking-item"
                  data-testid="booking-item-select"
                  className="lux-input"
                  value={form.item_id}
                  onChange={set("item_id")}
                  disabled={!catalog}
                >
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                      {i.model ? ` — ${i.model}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-6 sm:grid-cols-3">
                <div>
                  <label className="lux-label" htmlFor="booking-start">From</label>
                  <input id="booking-start" data-testid="booking-start-date" type="date" required
                    className="lux-input [color-scheme:dark]" value={form.start_date} onChange={set("start_date")} />
                </div>
                <div>
                  <label className="lux-label" htmlFor="booking-end">To</label>
                  <input id="booking-end" data-testid="booking-end-date" type="date" required
                    className="lux-input [color-scheme:dark]" value={form.end_date} onChange={set("end_date")} />
                </div>
                <div>
                  <label className="lux-label" htmlFor="booking-guests">Guests</label>
                  <input id="booking-guests" data-testid="booking-guests-input" type="number" min="1" max="12" required
                    className="lux-input" value={form.guests} onChange={set("guests")} />
                </div>
              </div>

              {form.type === "vehicle" && (
                <label className="flex cursor-pointer items-center gap-3 text-sm text-zinc-300" data-testid="booking-transfer-toggle">
                  <input type="checkbox" checked={form.airport_transfer} onChange={set("airport_transfer")}
                    className="h-4 w-4 accent-[#D4AF37]" />
                  Add private runway pickup at ATL (+${catalog?.airport_transfer_fee ?? 150})
                </label>
              )}

              <div className="grid gap-6 sm:grid-cols-3">
                <div>
                  <label className="lux-label" htmlFor="booking-name">Full Name</label>
                  <input id="booking-name" data-testid="booking-name-input" required className="lux-input"
                    placeholder="Jordan Carter" value={form.guest_name} onChange={set("guest_name")} />
                </div>
                <div>
                  <label className="lux-label" htmlFor="booking-email">Email</label>
                  <input id="booking-email" data-testid="booking-email-input" type="email" required className="lux-input"
                    placeholder="you@email.com" value={form.guest_email} onChange={set("guest_email")} />
                </div>
                <div>
                  <label className="lux-label" htmlFor="booking-phone">Phone</label>
                  <input id="booking-phone" data-testid="booking-phone-input" type="tel" required className="lux-input"
                    placeholder="(404) 555-0100" value={form.guest_phone} onChange={set("guest_phone")} />
                </div>
              </div>

              <div>
                <label className="lux-label" htmlFor="booking-notes">Notes for the Concierge (optional)</label>
                <textarea id="booking-notes" data-testid="booking-notes-input" rows="3" className="lux-input resize-none"
                  placeholder="Flight number, occasion, preferences…" value={form.notes} onChange={set("notes")} />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="spotlight-card sticky top-28 p-8" data-testid="booking-summary-card">
                <p className="eyebrow">Estimated Total</p>
                <p className="mt-4 font-serif text-5xl text-slate-100" data-testid="booking-total-price">
                  ${total.toLocaleString()}
                </p>
                <div className="mt-6 space-y-3 border-t border-gold/10 pt-6 font-mono text-[11px] tracking-wider text-zinc-400 uppercase">
                  <p className="flex justify-between"><span>Duration</span><span className="text-gold">{nights || "—"} {form.type === "condo" ? "nights" : "days"}</span></p>
                  <p className="flex justify-between"><span>Experience</span><span className="max-w-[55%] truncate text-right text-gold">{items.find((i) => i.id === form.item_id)?.name ?? "—"}</span></p>
                  {form.type === "condo" && <p className="flex justify-between"><span>Cleaning</span><span className="text-gold">${catalog?.condo.cleaning_fee ?? 150}</span></p>}
                  {form.type === "vehicle" && form.airport_transfer && <p className="flex justify-between"><span>Runway pickup</span><span className="text-gold">${catalog?.airport_transfer_fee ?? 150}</span></p>}
                </div>
                <button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={submitting || !catalog}
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] disabled:opacity-60"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Sending…" : "Request Booking"}
                </button>
                <p className="mt-4 text-center text-[11px] leading-relaxed text-zinc-600">
                  No payment taken now — our concierge confirms availability first.
                </p>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
